import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import { isEmpty } from 'lodash';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon, {
  resolveAttributionMethod,
} from '../../../../../../shared/designSystem/components/AttributionIcon';
import ImpressionSubTable from './ImpressionsTimeLine';
import SignupImpressions from './signupimpressions';

// =============================================================================
// SignUpsTable (V1) — migrated from PaginationTable to the shared DataTable
// primitive. Column model preserved: Sign Up Id · Last Impression · Impressions
// · Signed up on · attribution-icon. Attribution-method PNG sprites swapped
// for the shared `<AttributionIcon/>` Lucide primitive (mirrors V2
// Transactions / SignUps).
//
// Simplified-report branching preserved verbatim:
//   • non-simplified — client-side pagination, expanded row → ImpressionSubTable
//   • simplified     — manual pagination (parent owns page/idx via
//                      setCurrentPage / setCurrentIdx), expanded row →
//                      <SignupImpressions/> loader. URL pageSize sync kept.
// Numeric/sort logic preserved verbatim from the legacy implementation.
// =============================================================================

// Determines the latest impression timestamp on a row. Mirrors V1's reduce
// over `impressions[].IMPRESSION_TIME` with the legacy fallback to
// `row.latestDate` when the array is missing.
const latestImpressionTime = (row) => {
  if (row && row.impressions && row.impressions.length) {
    let latest = null;
    row.impressions.forEach((impression) => {
      const { IMPRESSION_TIME } = impression;
      if (!latest || latest < IMPRESSION_TIME) latest = IMPRESSION_TIME;
    });
    return latest;
  }
  return row && row.latestDate ? row.latestDate : null;
};

const sortByLatestImpression = (rowA, rowB) => {
  const a = latestImpressionTime(rowA.original);
  const b = latestImpressionTime(rowB.original);
  return new Date(a) - new Date(b);
};

const sortBySignupId = (rowA, rowB) =>
  String(rowA.original.signupId || '').localeCompare(String(rowB.original.signupId || ''));

const sortByImpressionCount = (rowA, rowB) => {
  const a = rowA.original.impressions ? rowA.original.impressions.length : 0;
  const b = rowB.original.impressions ? rowB.original.impressions.length : 0;
  return a - b;
};

const sortBySignupTimestamp = (rowA, rowB) => {
  const a = rowA.original.signups_tstamp;
  const b = rowB.original.signups_tstamp;
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return moment(a).valueOf() - moment(b).valueOf();
};

const renderImpressionCount = ({ row, getValue }) => {
  const impressions = getValue();
  const { totalImpressions, impressionsLength } = row.original;
  const impressionCount = isEmpty(impressions)
    ? 0
    : impressions.length || impressionsLength;
  const count =
    totalImpressions && totalImpressions > impressionCount
      ? totalImpressions
      : impressionCount;
  return <span>{count}</span>;
};

const SignUpsTable = ({
  signUpsData,
  signupTotalRecord,
  hasSimplifiedReport = false,
  reportName,
  campaignOrderId,
  isSimplified,
  setCurrentIdx,
  loading,
  currentPage,
  setCurrentPage,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [pageSize, setPageSize] = useState(10);

  const params = new URLSearchParams(location.search);
  if (!params.get('pageSize')) {
    params.delete('pageSize');
    params.append('pageSize', 10);
  }

  const rows = useMemo(
    () => (signUpsData || []).map((item, i) => ({ ...item, __key: i })),
    [signUpsData]
  );

  const columns = useMemo(
    () => [
      {
        id: 'signupId',
        accessorKey: 'signupId',
        header: 'Sign Up Id',
        sortable: true,
        sortingFn: sortBySignupId,
        meta: { align: 'left', width: 220 },
        cell: ({ getValue }) => (
          <span className="campaign-overview__creatives-name">{getValue() || '—'}</span>
        ),
      },
      {
        id: 'completeLastImpression',
        accessorKey: 'completeLastImpression',
        header: 'Last Impression',
        sortable: true,
        sortingFn: sortByLatestImpression,
        meta: { align: 'left', width: 180 },
        cell: ({ row }) => {
          const latest = latestImpressionTime(row.original);
          return (
            <span>
              {latest
                ? moment(latest, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm')
                : ''}
            </span>
          );
        },
      },
      {
        id: 'impressions',
        accessorKey: 'impressions',
        header: 'Impressions',
        sortable: true,
        sortingFn: sortByImpressionCount,
        meta: { align: 'right', width: 130 },
        cell: renderImpressionCount,
      },
      {
        id: 'signups_tstamp',
        accessorKey: 'signups_tstamp',
        header: 'Signed up on',
        sortable: true,
        sortingFn: sortBySignupTimestamp,
        meta: { align: 'left', width: 180 },
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <span>
              {v ? moment(v, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm') : '—'}
            </span>
          );
        },
      },
      {
        id: 'attributionMethod',
        accessorKey: 'attributionMethod',
        header: '',
        sortable: false,
        meta: { align: 'center', width: 70 },
        cell: ({ getValue }) => {
          const method = resolveAttributionMethod(getValue());
          return <AttributionIcon method={method || 'IP'} />;
        },
      },
    ],
    []
  );

  const renderMobileCard = (record) => {
    const latest = latestImpressionTime(record);
    const method = resolveAttributionMethod(record.attributionMethod);
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <div className="campaign-overview__creatives-card-name">
            <span>{record.signupId || '—'}</span>
          </div>
          <AttributionIcon method={method || 'IP'} />
        </div>
        <div className="campaign-overview__lineitem-card-meta">
          <span className="campaign-overview__lineitem-flight">
            {record.signups_tstamp
              ? moment(record.signups_tstamp, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm')
              : '—'}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {(() => {
                const impressionCount = isEmpty(record.impressions)
                  ? 0
                  : record.impressions.length || record.impressionsLength;
                return record.totalImpressions && record.totalImpressions > impressionCount
                  ? record.totalImpressions
                  : impressionCount;
              })()}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">
              Last impression
            </span>
            <span
              className="campaign-overview__creatives-card-metric-value"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              {latest
                ? moment(latest, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm')
                : '—'}
            </span>
          </div>
        </div>
      </>
    );
  };

  // Simplified-mode pagination owns currentPage / setCurrentIdx via parent
  // and syncs pageSize to URL. Non-simplified uses DataTable's client-side
  // pagination directly. Both paths preserved verbatim from the legacy
  // PaginationTable onChange handler.
  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    const nextPage = pageIndex + 1;
    setPageSize(newPageSize);
    setCurrentPage(nextPage);
    if (isSimplified) {
      if (Number(newPageSize) !== Number(pageSize)) {
        setCurrentPage(1);
        setCurrentIdx(0);
      } else {
        setCurrentIdx(Number(pageSize) * Math.abs(nextPage - 1));
      }
      if (params.get('pageSize')) params.delete('pageSize');
      if (newPageSize > 10) {
        params.append('pageSize', newPageSize);
      }
      history.push(`?${params.toString()}`);
    }
  };

  const total = isSimplified ? signupTotalRecord : (signUpsData ? signUpsData.length : 0);

  return (
    <DataTable
      data={rows}
      columns={columns}
      loading={loading}
      pageSize={pageSize}
      pageSizeOptions={[10, 20, 50, 100]}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      manualPagination={isSimplified}
      pageIndex={(currentPage || 1) - 1}
      totalRows={total}
      onPaginationChange={handlePaginationChange}
      expandable={{
        isRowExpandable: (record) =>
          hasSimplifiedReport ? true : Boolean(record && record.impressions),
        renderExpandedRow: (record) =>
          !hasSimplifiedReport ? (
            <ImpressionSubTable impressions={record.impressions ? record.impressions : []} />
          ) : (
            <SignupImpressions
              reportName={reportName}
              campaignOrderId={campaignOrderId}
              signupId={record.signupId}
            />
          ),
      }}
      emptyState="No sign ups to show."
    />
  );
};

export default SignUpsTable;
